import QrScanner from "qr-scanner";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

export type QrScannerState = "idle" | "starting" | "running" | "denied" | "unavailable";

// The camera keeps seeing a passenger's QR for several seconds; reading it again in that time would
// answer "already used" for the passenger who was just let on.
const SAME_CODE_QUIET_MS = 8000;

// Reads QR codes from the rear camera. `onRead` returns false when it could not take the code (a check
// is still running), so the same code held up again is read on the next frame instead of being ignored;
// a check that resolves false (it failed) forgets the code too. qr-scanner pauses by itself while the tab
// is hidden.
export const useQrScanner = (
  videoRef: RefObject<HTMLVideoElement>,
  onRead: (text: string) => boolean | void | Promise<boolean>,
  enabled: boolean
) => {
  const [state, setState] = useState<QrScannerState>("idle");
  const [attempt, setAttempt] = useState(0);
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !video) return;

    let cancelled = false;
    let lastText = "";
    let lastAt = 0;
    const scanner = new QrScanner(
      video,
      (result) => {
        const now = Date.now();
        if (result.data === lastText && now - lastAt < SAME_CODE_QUIET_MS) return;
        const taken = onReadRef.current(result.data);
        if (taken === false) return;
        lastText = result.data;
        lastAt = now;
        if (taken instanceof Promise)
          void taken.then((checked) => {
            if (!checked && lastText === result.data) lastText = "";
          });
      },
      {
        preferredCamera: "environment",
        maxScansPerSecond: 5,
        highlightScanRegion: true,
        returnDetailedScanResult: true
      }
    );

    setState("starting");
    scanner
      .start()
      .then(() => !cancelled && setState("running"))
      .catch(async () => {
        // qr-scanner reports every camera failure as "Camera not found.", so ask the permission itself.
        const permission = await navigator.permissions
          ?.query({ name: "camera" as PermissionName })
          .catch(() => null);
        if (!cancelled) setState(permission?.state === "denied" ? "denied" : "unavailable");
      });

    return () => {
      cancelled = true;
      scanner.stop();
      scanner.destroy();
    };
  }, [enabled, videoRef, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { state, retry };
};

export default useQrScanner;
